import { useState, type CSSProperties, type ReactNode } from 'react';
import { useApp, Banner, NakedButton, Button, Card, Pill, Icon, Input } from '@/components/ds';
import { Method, CodeBlock, CopyChip, Toggle } from '@/components/code';
import { jsonStr } from '@/lib/codegen';
import { RES, DASH, LIVE_KEY, TEST_KEY } from '@/data';
import { PageTitle, Table, Drawer } from '@/containers/dashboard';
import { DefList } from '@/containers/dashboard-businesses';
import { KeyReveal } from '@/containers/onboarding';
import { PreviewNote, PreviewBadge } from '@/components/live';

// dashboard3.tsx — Developers: API keys, Logs, Events, Webhooks.
// All four are PREVIEW: the API exposes no key-management, request-log, event, or webhook
// endpoints yet (see the API-support TODO). Screens render the design with illustrative data.

// ====== API KEYS =========================================================
function KeysView() {
  const app = useApp();
  const [created, setCreated] = useState<string | null>(null); // newly created key reveal
  const [rotate, setRotate] = useState<any | null>(null);
  const keys = DASH.keys;
  return (
    <div>
      <PageTitle
        title={
          <>
            API keys <PreviewBadge />
          </>
        }
        sub="Secret keys authenticate your requests. Keep them server-side."
        action={
          <Button leftIcon="add" onClick={() => setCreated(app.isLive ? LIVE_KEY : TEST_KEY)}>
            Create key
          </Button>
        }
      />
      <PreviewNote>Key listing and rotation need a partner-facing keys endpoint (only key creation at signup exists today).</PreviewNote>
      <Banner kind="brand" style={{ marginBottom: 20 }}>
        Secret keys are shown in full only once, at creation. After that you'll see the prefix. Lost a key? Rotate it.
      </Banner>
      <Table
        columns={[
          {
            key: 'label',
            label: 'Name',
            render: (r) => (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Icon name="lock" size={16} color="var(--neutral-600)" />
                <span style={{ fontWeight: 600 }}>{r.label}</span>
              </div>
            ),
          },
          {
            key: 'prefix',
            label: 'Token',
            render: (r) => (
              <code className="om-mono" style={{ fontSize: 13 }}>
                {r.prefix}••••••••
              </code>
            ),
          },
          {
            key: 'env',
            label: 'Mode',
            render: (r) => (
              <Pill kind={r.env === 'live' ? 'critical' : 'neutral'} dot>
                {r.env === 'live' ? 'Live' : 'Test'}
              </Pill>
            ),
          },
          {
            key: 'lastUsed',
            label: 'Last used',
            render: (r) => <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{r.lastUsed}</span>,
          },
          { key: 'note', label: 'Note', render: (r) => <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{r.note}</span> },
          {
            key: 'x',
            label: '',
            align: 'right',
            render: (r) => (
              <NakedButton onClick={() => setRotate(r)} color="var(--accent)">
                Rotate
              </NakedButton>
            ),
          },
        ]}
        rows={keys}
      />

      {/* create / reveal */}
      <Drawer open={!!created} onClose={() => setCreated(null)} title="New API key">
        {created && <KeyReveal apiKey={created} onContinue={() => setCreated(null)} />}
      </Drawer>

      {/* rotate confirm */}
      <Drawer
        open={!!rotate}
        onClose={() => setRotate(null)}
        title="Rotate key"
        footer={
          <>
            <Button variant="tertiary" onClick={() => setRotate(null)}>
              Cancel
            </Button>
            <Button
              variant="critical"
              onClick={() => {
                setRotate(null);
                setCreated(rotate.env === 'live' ? LIVE_KEY : TEST_KEY);
              }}
            >
              Rotate now
            </Button>
          </>
        }
      >
        {rotate && (
          <div>
            <p style={{ fontSize: 15, color: 'var(--text-secondary)', marginTop: 0 }}>
              Rotating <strong style={{ color: 'var(--text-primary)' }}>{rotate.label}</strong> issues a new secret immediately.
            </p>
            <Card padding={18} style={{ background: 'var(--neutral-200)', border: 'none', display: 'flex', gap: 12 }}>
              <Icon name="clock" size={20} color="var(--accent)" />
              <div>
                <div style={{ fontWeight: 600, fontSize: 14 }}>24-hour grace window</div>
                <div style={{ fontSize: 13.5, color: 'var(--text-secondary)', marginTop: 2 }}>
                  The old key keeps working for 24 hours so you can roll out the new one without downtime. After that it's revoked.
                </div>
              </div>
            </Card>
          </div>
        )}
      </Drawer>
    </div>
  );
}

// ====== LOGS =============================================================
function statusColor(code: number): string {
  return code < 300 ? 'success' : code < 400 ? 'informative' : code < 500 ? 'warning' : 'critical';
}

interface LogDetailProps {
  log: any;
  style?: CSSProperties;
}
function LogDetail({ log }: LogDetailProps) {
  const ok = log.status < 400;
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <Method m={log.method} />
        <code className="om-mono" style={{ fontSize: 13.5, wordBreak: 'break-all' }}>
          {log.path}
        </code>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20, fontSize: 13, color: 'var(--text-secondary)' }}>
        <span className="om-mono" style={{ color: ok ? 'var(--success-700)' : 'var(--critical-700)', fontWeight: 700 }}>
          ● {log.status}
        </span>
        <span>{log.ms} ms</span>
        <span className="om-mono">{log.id}</span>
        <span>{log.time}</span>
      </div>
      <div
        style={{
          fontSize: 12.5,
          fontWeight: 600,
          color: 'var(--text-secondary)',
          marginBottom: 8,
          textTransform: 'uppercase',
          letterSpacing: '.04em',
        }}
      >
        Request body
      </div>
      <CodeBlock
        dark
        code={jsonStr(
          log.method === 'POST' && log.path.includes('payments')
            ? { amount: 125000, originatingAccountId: 'acct_9a2f7', receivingAccountId: 'acct_7c4b1', deductionDate: '2026-06-10' }
            : {}
        )}
        maxHeight={160}
      />
      <div
        style={{
          fontSize: 12.5,
          fontWeight: 600,
          color: 'var(--text-secondary)',
          margin: '16px 0 8px',
          textTransform: 'uppercase',
          letterSpacing: '.04em',
        }}
      >
        Response
      </div>
      <CodeBlock dark code={jsonStr(ok ? RES.payment : RES.paymentEligibilityError)} maxHeight={220} />
    </div>
  );
}

function LogsView() {
  const [detail, setDetail] = useState<any | null>(null);
  return (
    <div>
      <PageTitle
        title={
          <>
            Logs <PreviewBadge />
          </>
        }
        sub="Every API request, with latency and request id. Reachable from any key."
      />
      <PreviewNote>A request-log inspector needs a partner-facing log store the backend doesn't have yet.</PreviewNote>
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center' }}>
        <div style={{ flex: 1, maxWidth: 320 }}>
          <Input prefix="" placeholder="Filter by path, status, or request id" />
        </div>
        <Button variant="tertiary" leftIcon="filter" size="small">
          Filters
        </Button>
      </div>
      <Table
        onRow={(r) => setDetail(r)}
        columns={[
          { key: 'method', label: 'Method', render: (r) => <Method m={r.method} /> },
          {
            key: 'path',
            label: 'Path',
            render: (r) => (
              <code className="om-mono" style={{ fontSize: 12.5 }}>
                {r.path}
              </code>
            ),
          },
          {
            key: 'status',
            label: 'Status',
            render: (r) => (
              <span className="om-mono" style={{ fontWeight: 700, color: `var(--${statusColor(r.status)}-700)` }}>
                {r.status}
              </span>
            ),
          },
          {
            key: 'ms',
            label: 'Latency',
            align: 'right',
            render: (r) => (
              <span className="om-mono" style={{ color: r.ms > 300 ? 'var(--warning-900)' : 'var(--text-secondary)' }}>
                {r.ms} ms
              </span>
            ),
          },
          {
            key: 'time',
            label: 'Time',
            align: 'right',
            render: (r) => (
              <span className="om-mono" style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
                {r.time}
              </span>
            ),
          },
          {
            key: 'id',
            label: 'Request id',
            align: 'right',
            render: (r) => (
              <code className="om-mono" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                {r.id}
              </code>
            ),
          },
        ]}
        rows={DASH.logs}
      />
      <Drawer open={!!detail} onClose={() => setDetail(null)} title="Request log">
        {detail && <LogDetail log={detail} />}
      </Drawer>
    </div>
  );
}

// ====== EVENTS ===========================================================
interface EventDetailProps {
  evt: any;
  style?: CSSProperties;
}
function EventDetail({ evt }: EventDetailProps) {
  const ok = evt.code < 400;
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <code className="om-mono" style={{ fontSize: 15, fontWeight: 600 }}>
          {evt.type}
        </code>
        <Pill kind={ok ? 'success' : 'critical'}>{ok ? 'Delivered' : 'Failed'}</Pill>
      </div>
      <Card padding={18} style={{ marginBottom: 16 }}>
        <DefList
          rows={[
            ['Event id', evt.id],
            ['Related payment', evt.payment],
            ['Your endpoint', 'https://api.partner.co/webhooks/melio'],
            ['Response code', String(evt.code)],
            ['Attempts', String(evt.attempts)],
            ['Delivered at', evt.time],
          ]}
        />
      </Card>
      <div
        style={{
          fontSize: 12.5,
          fontWeight: 600,
          color: 'var(--text-secondary)',
          marginBottom: 8,
          textTransform: 'uppercase',
          letterSpacing: '.04em',
        }}
      >
        Payload
      </div>
      <CodeBlock
        dark
        code={jsonStr({
          id: evt.id,
          type: evt.type,
          created: '2026-05-28T09:31:48Z',
          data: { object: 'payment', id: evt.payment, status: evt.type.split('.')[1] },
        })}
        maxHeight={240}
      />
    </div>
  );
}

function EventsView() {
  const [detail, setDetail] = useState<any | null>(null);
  const [retried, setRetried] = useState<Record<string, boolean>>({});
  return (
    <div>
      <PageTitle
        title={
          <>
            Events <PreviewBadge />
          </>
        }
        sub="Webhook events with delivery status. Inspect payloads and re-trigger any event."
      />
      <PreviewNote>Events depend on payment-status webhooks, which aren't in scope yet.</PreviewNote>
      <Table
        onRow={(r) => setDetail(r)}
        columns={[
          {
            key: 'type',
            label: 'Event',
            render: (r) => (
              <code className="om-mono" style={{ fontSize: 13, fontWeight: 600 }}>
                {r.type}
              </code>
            ),
          },
          {
            key: 'payment',
            label: 'Resource',
            render: (r) => (
              <code className="om-mono" style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
                {r.payment}
              </code>
            ),
          },
          {
            key: 'status',
            label: 'Delivery',
            render: (r) => (
              <Pill kind={r.code < 400 ? 'success' : 'critical'}>{r.code < 400 ? 'Delivered' : `Failed · ${r.attempts}×`}</Pill>
            ),
          },
          {
            key: 'time',
            label: 'Time',
            align: 'right',
            render: (r) => (
              <span className="om-mono" style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
                {r.time}
              </span>
            ),
          },
          {
            key: 'x',
            label: '',
            align: 'right',
            render: (r) => (
              <NakedButton
                color="var(--accent)"
                onClick={(e) => {
                  e?.stopPropagation?.();
                  setRetried((s) => ({ ...s, [r.id]: true }));
                }}
              >
                {retried[r.id] ? 'Re-sent ✓' : 'Re-trigger'}
              </NakedButton>
            ),
          },
        ]}
        rows={DASH.events}
      />
      <Drawer open={!!detail} onClose={() => setDetail(null)} title="Event">
        {detail && <EventDetail evt={detail} />}
      </Drawer>
    </div>
  );
}

// ====== WEBHOOKS =========================================================
function WebhooksView() {
  const events: string[] = [
    'api.entity.created',
    'api.entity.updated',
    'api.account.created',
    'api.account.updated',
    'api.account.deleted',
    'api.payment.created',
  ];
  const [subbed, setSubbed] = useState<Record<string, boolean>>({
    'api.payment.created': true,
    'api.account.created': true,
    'api.account.updated': true,
  });
  return (
    <div>
      <PageTitle
        title={
          <>
            Webhooks <PreviewBadge />
          </>
        }
        sub="Get notified when payments change state. No polling required."
        action={
          <Button leftIcon="add" disabled>
            Add endpoint
          </Button>
        }
      />
      <PreviewNote>Webhook registration + delivery need a webhooks subsystem that isn't built yet.</PreviewNote>
      <Card padding={22} style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div
            style={{
              width: 42,
              height: 42,
              borderRadius: 10,
              background: 'var(--success-100)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon name="fast" size={22} color="var(--success-700)" />
          </div>
          <div style={{ flex: 1 }}>
            <code className="om-mono" style={{ fontSize: 14, fontWeight: 600 }}>
              https://api.partner.co/webhooks/melio
            </code>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>3 events · 100% delivered in last 24h</div>
          </div>
          <Pill kind="success">Active</Pill>
        </div>
        <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border-static)' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>Signing secret</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <code
              className="om-mono"
              style={{ flex: 1, fontSize: 13, background: 'var(--neutral-200)', padding: '8px 12px', borderRadius: 8 }}
            >
              whsec_2kP9xQ••••••••••••
            </code>
            <CopyChip text="whsec_2kP9xQ..." dark={false} label="Copy" />
          </div>
        </div>
      </Card>
      <h3 style={{ fontSize: 16, margin: '0 0 12px' }}>Subscribed events</h3>
      <Card padding={8}>
        {events.map((e, i) => (
          <div
            key={e}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '12px 14px',
              borderTop: i ? '1px solid var(--border-static)' : 'none',
            }}
          >
            <code className="om-mono" style={{ flex: 1, fontSize: 13.5 }}>
              {e}
            </code>
            <Toggle value={!!subbed[e]} onChange={(v: boolean) => setSubbed((s) => ({ ...s, [e]: v }))} />
          </div>
        ))}
      </Card>
    </div>
  );
}

export { KeysView, LogsView, LogDetail, EventsView, EventDetail, WebhooksView, statusColor };
